import React, { useState, useEffect, useMemo } from 'react';
import { Plus, X, Pencil, Trash2, Users, TrendingUp, LogOut, Save, ShieldCheck, ShieldAlert, Lock, Calculator, ChevronDown, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getColaboradores, postColaborador, putColaborador, deleteColaborador,
  getReajustes, postReajuste, deleteReajuste, getSalarioMensal,
  getSalarioMensalTodos, getRoleFromToken,
  getDespesas, postDespesa, putDespesa, deleteDespesa,
} from '../../api';
import { DARK_THEME_ORCAMENTO, LIGHT_THEME_ORCAMENTO, ThemeToggle } from '../../componets/temas';
import SideBarNavOrcamento from '../../componets/SideBarNavOrcamento';

// ── Constantes ─────────────────────────────────────────────────────────────────

const MESES_ABREV = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MESES_FULL  = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const ANO_ATUAL   = new Date().getFullYear();

const DEPARTAMENTOS = [
  'CONSELHO',
  'DESENVOLVIMENTO TECNOLÓGICO',
  'DIRETORIA ADMINISTRATIVO FINANCEIRO',
  'DIRETORIA COMERCIAL',
  'DIRETORIA DE DESENVOLVIMENTO DE NEGÓCIOS',
  'DIRETORIA DE PESSOAS E CULTURA',
  'DIRETORIA DE PROCESSOS E QUALIDADE',
  'DIRETORIA EXECUTIVA',
  'DIRETORIA JURÍDICA',
  'DIRETORIA MARKETING',
  'OPERAÇÃO',
];

const TIPOS_CONTRATO = ['CLT', 'PJ', 'Estagiário'];

const TIPOS_DESPESA = [
  'Marketing e Publicidade',
  'Despesas de Viagens',
  'Administrativas Gerais',
  'Equipamentos e Materiais',
];

const CONTRATO_COLORS = {
  CLT:        { bg: '#6366F122', text: '#6366F1' },
  PJ:         { bg: '#F59E0B22', text: '#D97706' },
  Estagiário: { bg: '#10B98122', text: '#059669' },
};

const TABELA_REAJUSTE = {
  '80%':  [1595, 1595, 1851, 2323, 2915, 3657, 4589, 5757, 7224, 9064, 11372, 14268, 17902, 22462, 28183],
  '85%':  [1595, 1595, 1967, 2469, 3097, 3886, 4876, 6117, 7676, 9630, 12083, 15160, 19021, 23865, 29944],
  '90%':  [1595, 1595, 2083, 2614, 3279, 4115, 5163, 6477, 8127, 10197, 12793, 16052, 20140, 25269, 31706],
  '95%':  [1575, 1753, 2198, 2759, 3461, 4343, 5449, 6837, 8579, 10763, 13504, 16944, 21259, 26673, 33467],
  '100%': [1658, 1845, 2314, 2904, 3644, 4572, 5736, 7197, 9030, 11330, 14215, 17835, 22378, 28077, 35229],
  '105%': [1741, 1937, 2430, 3050, 3826, 4800, 6023, 7557, 9482, 11896, 14926, 18727, 23496, 29481, 36990],
  '110%': [1824, 2029, 2546, 3195, 4008, 5029, 6310, 7916, 9933, 12462, 15636, 19619, 24615, 30885, 38751],
  '115%': [1907, 2122, 2661, 3340, 4190, 5257, 6597, 8276, 10385, 13029, 16347, 20511, 25734, 32289, 40513],
  '120%': [1990, 2214, 2777, 3485, 4372, 5486, 6883, 8636, 10836, 13595, 17058, 21402, 26853, 33692, 42274],
};

const TAB_COLUMNS = Object.keys(TABELA_REAJUSTE);
const TAB_ROWS    = Array.from({ length: 15 }, (_, i) => i);

const ROLES_FULL_ACCESS = ['ADMIN'];

const ROLE_TO_DEPARTAMENTO = {
  CONSELHO:             ['CONSELHO'],
  MARKETING:            ['DIRETORIA MARKETING'],
  TI:                   ['DESENVOLVIMENTO TECNOLÓGICO', 'OPERAÇÃO'],
  ADMFIN:               ['DIRETORIA ADMINISTRATIVO FINANCEIRO'],
  NEGOCIOS:             ['DIRETORIA DE DESENVOLVIMENTO DE NEGÓCIOS'],
  EXECUTIVA:            ['DIRETORIA EXECUTIVA'],
  QUALIDADE:            ['DIRETORIA DE PROCESSOS E QUALIDADE'],
  RH:                   ['DIRETORIA DE PESSOAS E CULTURA'],
  JURIDICO:             ['DIRETORIA JURÍDICA'],
  OPERACAO:             ['OPERAÇÃO'],
  ASSISTENTEOPERACAO:   ['OPERAÇÃO'],
  COMERCIAL:            ['DIRETORIA COMERCIAL'],
  FACILITIES:           ['DIRETORIA ADMINISTRATIVO FINANCEIRO'],
  'PESSOA&CULTURA':     ['DIRETORIA DE PESSOAS E CULTURA'],
  GESTOR_ESTRATEGICO:   ['DIRETORIA JURÍDICA', 'DIRETORIA DE PROCESSOS E QUALIDADE', 'DIRETORIA EXECUTIVA'],
};

// ── Helpers de formatação ───────────────────────────────────────────────────────

// YYYY-MM → YYYY-MM-01  (para enviar ao backend como Date)
const toApiDate  = (m) => m ? `${m}-01` : null;
// YYYY-MM-DD → YYYY-MM  (para preencher <input type="month">)
const toFormMonth = (d) => d ? String(d).substring(0, 7) : '';
// Formata vigência: "Jan/25 – ∞"
function fmtVigencia(inicio, fim) {
  const fmt = (d) => {
    if (!d) return '∞';
    const dt = new Date(d + 'T00:00:00');
    return `${MESES_ABREV[dt.getMonth()]}/${String(dt.getFullYear()).slice(2)}`;
  };
  return `${fmt(inicio)} – ${fmt(fim)}`;
}

const fmt    = (v) => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtK   = (v) => {
  if (!v && v !== 0) return '–';
  if (Math.abs(v) >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `R$ ${(v / 1_000).toFixed(2)}k`;
  return `R$ ${v.toFixed(0)}`;
};
const getDataAtualizacao = () =>
  new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

// ── Componente principal ────────────────────────────────────────────────────────

export default function Colaboradores() {
  const navigate  = useNavigate();
  const [isLight, setIsLight] = useState(true);
  const C = isLight ? LIGHT_THEME_ORCAMENTO : DARK_THEME_ORCAMENTO;

  // Controle de acesso
  const roleAtual     = (getRoleFromToken() ?? '').toUpperCase();
  const isAdmin       = ROLES_FULL_ACCESS.includes(roleAtual);
  const depPermitidos = isAdmin ? null : (ROLE_TO_DEPARTAMENTO[roleAtual] ?? null);
  const podeGerenciar = isAdmin || (depPermitidos && depPermitidos.length > 0);

  // Estado principal
  const [colaboradores,        setColaboradores]        = useState([]);
  const [loading,              setLoading]              = useState(true);
  const [salariosMensaisTodos, setSalariosMensaisTodos] = useState({});
  const [loadingBudget,        setLoadingBudget]        = useState(false);

  // Modais
  const [modalCadastro,              setModalCadastro]              = useState(false);
  const [modalSimulador,             setModalSimulador]             = useState(false);
  const [mostrarModalDetalhamento,   setmostrarModalDetalhamento]   = useState(false);
  const [colaboradorSel,             setColaboradorSel]             = useState(null);
  const [editando,                   setEditando]                   = useState(null);

  // Form
  const mesAtual = new Date().toISOString().substring(0, 7);
  const [form,    setForm]    = useState({ nome: '', salario: '', departamento: '', tipo_contrato: 'CLT', data_inicio: mesAtual, data_fim: '' });
  const [erro,    setErro]    = useState('');
  const [salvando, setSalvando] = useState(false);

  // Simulador
  const [reajustes,          setReajustes]          = useState([]);
  const [novoReajuste,       setNovoReajuste]       = useState({ mes: 5, ano: ANO_ATUAL, enquadramento: 1, agrupamento: '100%' });
  const [salariosMensais,    setSalariosMensais]    = useState(Array(12).fill(0));
  const [carregandoSim,      setCarregandoSim]      = useState(false);
  const [salvandoReajuste,   setSalvandoReajuste]   = useState(false);

  // Despesas
  const [despesas,         setDespesas]         = useState([]);
  const [loadingDespesas,  setLoadingDespesas]  = useState(true);
  const [modalDespesa,     setModalDespesa]     = useState(false);
  const [editandoDespesa,  setEditandoDespesa]  = useState(null);
  const depDefaultDespesa = !isAdmin && depPermitidos?.length >= 1 ? depPermitidos[0] : '';
  const [formDespesa,           setFormDespesa]           = useState({ nome: '', descricao: '', valor: '', departamento: depDefaultDespesa });
  const [erroDespesa,           setErroDespesa]           = useState('');
  const [salvandoDespesa,       setSalvandoDespesa]       = useState(false);
  const [categoriasExpandidas,  setCategoriasExpandidas]  = useState(new Set());

  useEffect(() => {
    carregarColaboradores();
    carregarBudget();
    carregarDespesas();
  }, []); // eslint-disable-line

  // ── Data fetching ─────────────────────────────────────────────────────────────

  async function carregarColaboradores() {
    try {
      const res = await getColaboradores();
      setColaboradores(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function carregarBudget() {
    setLoadingBudget(true);
    try {
      const res = await getSalarioMensalTodos(ANO_ATUAL);
      setSalariosMensaisTodos(res.data ?? {});
    } catch (e) {
      console.error('Budget error:', e);
    } finally {
      setLoadingBudget(false);
    }
  }

  // ── Despesas ──────────────────────────────────────────────────────────────────

  async function carregarDespesas() {
    setLoadingDespesas(true);
    try {
      const res = await getDespesas();
      setDespesas(res);
    } catch (e) {
      console.error('Despesas error:', e);
    } finally {
      setLoadingDespesas(false);
    }
  }

  function abrirDespesa(desp = null, categoriaPreSelecionada = '') {
    const depDef = !isAdmin && depPermitidos?.length >= 1 ? depPermitidos[0] : '';
    setEditandoDespesa(desp ? desp.id : null);
    setFormDespesa(
      desp
        ? { nome: desp.nome, descricao: desp.descricao || '', valor: String(desp.valor), departamento: desp.departamento }
        : { nome: categoriaPreSelecionada, descricao: '', valor: '', departamento: depDef }
    );
    setErroDespesa('');
    setModalDespesa(true);
  }

  function toggleCategoria(cat) {
    setCategoriasExpandidas((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  }

  async function salvarDespesa() {
    if (!formDespesa.nome.trim() || !formDespesa.valor || !formDespesa.departamento) {
      setErroDespesa('Preencha todos os campos obrigatórios.');
      return;
    }
    setSalvandoDespesa(true);
    try {
      const payload = {
        nome: formDespesa.nome.trim(),
        descricao: formDespesa.descricao.trim() || null,
        valor: parseFloat(formDespesa.valor),
        departamento: formDespesa.departamento,
      };
      editandoDespesa
        ? await putDespesa(editandoDespesa, payload)
        : await postDespesa(payload);
      await carregarDespesas();
      setModalDespesa(false);
    } catch (e) {
      setErroDespesa(e.message || 'Erro ao salvar.');
    } finally {
      setSalvandoDespesa(false);
    }
  }

  async function excluirDespesa(id) {
    if (!window.confirm('Deseja excluir esta despesa?')) return;
    try {
      await deleteDespesa(id);
      await carregarDespesas();
    } catch (e) {
      alert(e.message || 'Erro ao excluir.');
    }
  }

  // ── Cadastro ──────────────────────────────────────────────────────────────────

  function abrirCadastro(colab = null) {
    // Para não-admin com um único departamento, preenche automaticamente
    const depDefault = !isAdmin && depPermitidos?.length >= 1 ? depPermitidos[0] : '';
    setEditando(colab ? colab.id : null);
    setForm(
      colab
        ? {
            nome: colab.nome,
            salario: String(colab.salario),
            departamento: colab.departamento,
            tipo_contrato: colab.tipo_contrato || 'CLT',
            data_inicio: toFormMonth(colab.data_inicio),
            data_fim: toFormMonth(colab.data_fim),
          }
        : { nome: '', salario: '', departamento: depDefault, tipo_contrato: 'CLT', data_inicio: mesAtual, data_fim: '' }
    );
    setErro('');
    setModalCadastro(true);
  }

  async function salvarColaborador() {
    if (!form.nome.trim() || !form.salario || !form.departamento || !form.tipo_contrato || !form.data_inicio) {
      setErro('Preencha todos os campos obrigatórios (nome, salário, departamento, tipo de contrato e início de vigência).');
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        salario: parseFloat(form.salario),
        departamento: form.departamento,
        tipo_contrato: form.tipo_contrato,
        data_inicio: toApiDate(form.data_inicio),
        data_fim: toApiDate(form.data_fim) || null,
      };
      editando ? await putColaborador(editando, payload) : await postColaborador(payload);
      await Promise.all([carregarColaboradores(), carregarBudget()]);
      setModalCadastro(false);
    } catch (e) {
      setErro(e.message || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  async function inativarColaborador(id) {
    if (!window.confirm('Deseja inativar este colaborador?')) return;
    try {
      await deleteColaborador(id);
      await Promise.all([carregarColaboradores(), carregarBudget()]);
    } catch (e) {
      alert(e.message || 'Erro ao inativar.');
    }
  }

  // ── Simulador ─────────────────────────────────────────────────────────────────

  async function abrirSimulador(colab) {
    setColaboradorSel(colab);
    setNovoReajuste({ mes: 5, ano: ANO_ATUAL, enquadramento: 1, agrupamento: '100%' });
    setModalSimulador(true);
    await carregarSimulador(colab);
  }

  async function carregarSimulador(colab) {
    setCarregandoSim(true);
    try {
      const [listaReaj, mensal] = await Promise.all([
        getReajustes(colab.id),
        getSalarioMensal(colab.id, ANO_ATUAL),
      ]);
      setReajustes(listaReaj);
      setSalariosMensais(mensal.meses.map((m) => m.salario));
    } catch (e) {
      console.error(e);
    } finally {
      setCarregandoSim(false);
    }
  }

  async function salvarReajuste() {
    const salarioNovo = TABELA_REAJUSTE[novoReajuste.agrupamento]?.[novoReajuste.enquadramento - 1];
    if (!salarioNovo) { alert('Selecione um enquadramento e agrupamento válidos.'); return; }
    setSalvandoReajuste(true);
    try {
      await postReajuste({
        colaborador_id: colaboradorSel.id,
        mes_reajuste:   novoReajuste.mes,
        ano:            novoReajuste.ano,
        salario_novo:   salarioNovo,
      });
      await Promise.all([carregarSimulador(colaboradorSel), carregarBudget()]);
      setNovoReajuste({ mes: 5, ano: ANO_ATUAL, enquadramento: 1, agrupamento: '100%' });
    } catch (e) {
      alert(e.message || 'Erro ao salvar reajuste.');
    } finally {
      setSalvandoReajuste(false);
    }
  }

  async function removerReajuste(id) {
    if (!window.confirm('Remover este reajuste?')) return;
    try {
      await deleteReajuste(id);
      await Promise.all([carregarSimulador(colaboradorSel), carregarBudget()]);
    } catch (e) {
      alert(e.message || 'Erro ao remover.');
    }
  }

  // ── Dados derivados ───────────────────────────────────────────────────────────

  const colaboradoresFiltrados = useMemo(() => {
    if (!depPermitidos) return colaboradores;
    const normDep = depPermitidos.map((d) => d.toLowerCase());
    return colaboradores.filter((c) => normDep.includes((c.departamento ?? '').toLowerCase()));
  }, [colaboradores, depPermitidos]);

  const totaisMensais = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) =>
      colaboradoresFiltrados.reduce((acc, c) => {
        const meses = salariosMensaisTodos[String(c.id)];
        return acc + (meses ? meses[i] : c.salario);
      }, 0)
    );
  }, [colaboradoresFiltrados, salariosMensaisTodos]);

  const totalGeral = useMemo(() => totaisMensais.reduce((a, b) => a + b, 0), [totaisMensais]);
  const totalAnual = useMemo(() => salariosMensais.reduce((a, b) => a + b, 0), [salariosMensais]);

  const despesasFiltradas = useMemo(() => {
    if (!depPermitidos) return despesas;
    const normDep = depPermitidos.map((d) => d.toLowerCase());
    return despesas.filter((d) => normDep.includes((d.departamento ?? '').toLowerCase()));
  }, [despesas, depPermitidos]);

  const totalDespesas = useMemo(
    () => despesasFiltradas.reduce((a, d) => a + (d.valor ?? 0), 0),
    [despesasFiltradas]
  );

  const kpiMarketing = useMemo(
    () => despesasFiltradas.filter(d => d.nome === 'Marketing e Publicidade').reduce((a, d) => a + (d.valor ?? 0), 0),
    [despesasFiltradas]
  );
  const kpiViagens = useMemo(
    () => despesasFiltradas.filter(d => d.nome === 'Despesas de Viagens').reduce((a, d) => a + (d.valor ?? 0), 0),
    [despesasFiltradas]
  );
  const kpiAdmin = useMemo(
    () => despesasFiltradas.filter(d => d.nome === 'Administrativas Gerais').reduce((a, d) => a + (d.valor ?? 0), 0),
    [despesasFiltradas]
  );
  const kpiEquip = useMemo(
    () => despesasFiltradas.filter(d => d.nome === 'Equipamentos e Materiais').reduce((a, d) => a + (d.valor ?? 0), 0),
    [despesasFiltradas]
  );
  const kpiTotalGeral = useMemo(
    () => totalGeral + totalDespesas,
    [totalGeral, totalDespesas]
  );

  // ── Estilos ───────────────────────────────────────────────────────────────────

  const inputStyle = {
    width: '100%', background: C.deepBg, border: `1px solid ${C.border}`,
    borderRadius: 8, padding: '10px 14px', color: C.text, fontSize: 13,
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
  };
  const inputLocked = { ...inputStyle, background: C.inputDisabled, cursor: 'not-allowed', opacity: 0.75 };
  const btnPrimary   = { flex: 1, background: C.indigo, border: 'none', borderRadius: 8, padding: '10px 0', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
  const btnSecondary = { flex: 1, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 0', color: C.text, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };

  // Cabeçalho das colunas mensais: sticky para as 2 primeiras
  const thFixed = (left) => ({
    padding: '11px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: C.muted, borderBottom: `1px solid ${C.border}`,
    background: C.panelBg, position: 'sticky', left, zIndex: 2, whiteSpace: 'nowrap',
  });
  const thMonth = {
    padding: '11px 5px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: C.muted, borderBottom: `1px solid ${C.border}`,
    background: C.panelBg, textAlign: 'center', minWidth: 68, whiteSpace: 'nowrap',
  };
  const tdFixed = (left, bg) => ({
    padding: '13px 14px', background: bg, position: 'sticky', left,
    zIndex: 1, borderTop: `1px solid ${C.border}`, whiteSpace: 'nowrap',
  });

  // ── Render ────────────────────────────────────────────────────────────────────
  const trunc = (str, n = 20) => (str?.length > n ? str.slice(0, n) + '...' : str ?? '');

  return (
    <div style={{ minHeight: '100vh', background: C.darkBg, color: C.text, display: 'flex', flexDirection: 'row', fontFamily: 'inherit' }}>
      <SideBarNavOrcamento isLight={isLight} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header style={{ background: C.headerGradient, padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.3)', flexShrink: 0 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff' }}>Orçamento da Direção</h1>
          <p style={{ margin: '2px 0 0', fontSize: 10, color: C.headerText, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Atualizado em {getDataAtualizacao()} · {ANO_ATUAL}
          </p>
        </div>
           <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 999, padding: '5px 12px' }}>
            {isAdmin
              ? <><ShieldCheck size={13} color="#86efac" /><span style={{ fontSize: 11, fontWeight: 600, color: '#86efac' }}>Acesso Total</span></>
              : <><ShieldAlert size={13} color="#fde68a" /><span style={{ fontSize: 11, fontWeight: 600, color: '#fde68a', maxWidth: 1000, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ROLE_TO_DEPARTAMENTO[roleAtual] ? ROLE_TO_DEPARTAMENTO[roleAtual].join(', ') : 'Sem acesso'}</span></>
            }
          </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Badge de acesso */}
          {podeGerenciar && (
            <button
              onClick={() => abrirCadastro()}
              style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 999, padding: '8px 20px', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }}
            >
              <Plus size={14} /> Novo Colaborador
            </button>
          )}
          {podeGerenciar && (
            <button
              onClick={() => abrirDespesa()}
              style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 999, padding: '8px 20px', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }}
            >
              <Plus size={14} /> Nova Despesa
            </button>
          )}
          <button
            onClick={() => setmostrarModalDetalhamento(true)}
            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 999, padding: '8px 20px', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }}
          >
            <Calculator size={14} /> Tabela de Reajuste
          </button>
          <ThemeToggle isLight={isLight} onToggle={() => setIsLight((v) => !v)} />

          <button
            onClick={() => navigate('/inicio')}
            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 999, padding: '8px 16px', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }}
          >
            <LogOut size={14} /> Voltar
          </button>
        </div>
      </header>

      {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
      <div style={{ padding: '24px 24px 0' }}>
        <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[
            { label: 'Custo da Mão de Obra',     valor: totalGeral,      accent: C.indigo,   loading: loadingBudget },
            { label: 'Marketing e Publicidade',   valor: kpiMarketing,    accent: '#F59E0B',  loading: loadingDespesas },
            { label: 'Despesas de Viagens',       valor: kpiViagens,      accent: '#06B6D4',  loading: loadingDespesas },
            { label: 'Administrativas Gerais',    valor: kpiAdmin,        accent: '#10B981',  loading: loadingDespesas },
            { label: 'Equipamentos e Materiais',  valor: kpiEquip,        accent: '#3B82F6',  loading: loadingDespesas },
            { label: 'Total Geral',               valor: kpiTotalGeral,   accent: C.success,  loading: loadingBudget || loadingDespesas, isTotal: true },
          ].map(({ label, valor, accent, loading, isTotal }) => (
            <div key={label} style={{
              background: isTotal ? `${accent}18` : C.cardBg,
              border: `1px solid ${isTotal ? `${accent}55` : C.border}`,
              borderRadius: 12,
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              boxShadow: isTotal ? `0 4px 16px ${accent}20` : '0 2px 8px rgba(0,0,0,0.06)',
            }}>
              <div style={{ width: 28, height: 3, borderRadius: 2, background: accent, marginBottom: 6 }} />
              <p style={{ margin: 0, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted }}>
                {label}
              </p>
              <p style={{ margin: 0, fontFamily: 'monospace', fontWeight: 800, fontSize: isTotal ? 22 : 18, color: isTotal ? accent : C.text }}>
                {loading ? <span style={{ opacity: 0.3 }}>…</span> : fmt(valor)}
              </p>
            </div>
          ))}
        </div>
      </div>

      <main style={{ flex: 1, padding: '24px 24px 0' }}>
        <p style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Users size={14} /> Mão de Obra
        </p>
        {loading ? (
          <Spinner C={C} />
        ) : colaboradoresFiltrados.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 220, color: C.muted }}>
            <Users size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <p style={{ fontSize: 16, margin: '0 0 16px' }}>
              {colaboradores.length === 0 ? 'Nenhum colaborador cadastrado' : 'Nenhum colaborador visível para o seu perfil'}
            </p>
            {podeGerenciar && (
              <button onClick={() => abrirCadastro()} style={{ ...btnPrimary, flex: 'none', padding: '10px 24px' }}>
                Cadastrar primeiro colaborador
              </button>
            )}
          </div>
        ) : (
          <div style={{ borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
            {/* Scroll horizontal com colunas fixas */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {/* Colunas fixas (sticky) */}
                    <th style={{ ...thFixed(0), textAlign: 'left', minWidth: 150, maxWidth: 150 }}>Nome</th>
                    <th style={{ ...thFixed(100), textAlign: 'left', minWidth: 140, maxWidth: 130 }}>Departamento</th>
                    {/* Contrato e Vigência (não-sticky) */}
                    <th style={{ ...thMonth, minWidth: 100 }}>Contrato</th>
                    <th style={{ ...thMonth, minWidth: 120 }}>Vigência</th>
                    {/* Meses */}
                    {MESES_ABREV.map((m) => (
                      <th key={m} style={thMonth}>{m}</th>
                    ))}
                    {/* Total + Ações */}
                    <th style={{ ...thMonth, color: C.success }}>Total Anual</th>
                    <th style={{ ...thMonth, minWidth: 90 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {colaboradoresFiltrados.map((c, i) => {
                    const rowBg = i % 2 === 0 ? C.cardBg : C.rowAlt;
                    const meses = salariosMensaisTodos[String(c.id)];
                    const totalColab = meses ? meses.reduce((a, b) => a + b, 0) : c.salario * 12;
                    return (
                      <tr key={c.id}>
                        {/* ── Nome truncado ── */}
                        <td
                          title={c.nome}
                          style={{
                            ...tdFixed(0, rowBg),
                            fontWeight: 600,
                            maxWidth: 100,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {trunc(c.nome)}
                        </td>

                        {/* ── Departamento truncado ── */}
                        <td
                          title={c.departamento}
                          style={{
                            ...tdFixed(100, rowBg),
                            maxWidth: 100,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span style={{
                            background: `${C.indigo}22`,
                            color: C.indigo,
                            padding: '2px 5px',
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            verticalAlign: 'middle',
                          }}>
                            {trunc(c.departamento)}
                          </span>
                        </td>

                        {/* Tipo de contrato */}
                        <td style={{ padding: '12px 8px', textAlign: 'center', borderTop: `1px solid ${C.border}`, minWidth: 100 }}>
                          {(() => { const col = CONTRATO_COLORS[c.tipo_contrato] || CONTRATO_COLORS.CLT; return (
                            <span style={{ background: col.bg, color: col.text, padding: '2px 10px', borderRadius: 999, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {c.tipo_contrato || 'CLT'}
                            </span>
                          ); })()}
                        </td>
                        {/* Vigência */}
                        <td style={{ padding: '12px 8px', textAlign: 'center', borderTop: `1px solid ${C.border}`, minWidth: 120, fontFamily: 'monospace', fontSize: 10, color: C.muted, whiteSpace: 'nowrap' }}>
                          {fmtVigencia(c.data_inicio, c.data_fim)}
                        </td>
                        {Array.from({ length: 12 }, (_, mi) => {
                          let sal = meses ? meses[mi] : c.salario;

                          const checkSim = (enq, agrup, mes, mod) => {
                            if (enq !== null && mi >= MESES_ABREV.indexOf(mes)) {
                              const val = TABELA_REAJUSTE[agrup]?.[enq];
                              if (val) { sal = val; return true; }
                            }
                            return false;
                          };
                          if (!checkSim(c.r2_enq, c.r2_agrup, c.r2_mes, c.r2_mod)) {
                            checkSim(c.r1_enq, c.r1_agrup, c.r1_mes, c.r1_mod);
                          }

                          const anterior = mi === 0 ? c.salario : (meses ? meses[mi - 1] : c.salario);
                          const reajustado = sal > anterior;
                          return (
                            <td key={mi} style={{ padding: '12px 5px', textAlign: 'center', fontFamily: 'monospace', fontWeight: loadingBudget ? 400 : 600, fontSize: 11, color: reajustado ? C.success : C.text, background: reajustado ? `${C.success}12` : 'transparent', borderTop: `1px solid ${C.border}`, minWidth: 68 }}>
                              {loadingBudget ? <span style={{ opacity: 0.3 }}>…</span> : fmtK(sal)}
                            </td>
                          );
                        })}
                        {/* Total anual do colaborador */}
                        <td style={{ padding: '12px 6px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 700, fontSize: 11, color: C.success, borderTop: `1px solid ${C.border}`, minWidth: 90, whiteSpace: 'nowrap' }}>
                          {loadingBudget ? '…' : fmtK(totalColab)}
                        </td>
                        {/* Ações */}
                        <td style={{ padding: '10px 6px', borderTop: `1px solid ${C.border}`, minWidth: 90 }}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
                            <IconBtn title="Simular reajuste" onClick={() => abrirSimulador(c)} color={C.indigo} bg={`${C.indigo}22`}>
                              <TrendingUp size={13} />
                            </IconBtn>
                            {podeGerenciar && (
                              <IconBtn title="Editar" onClick={() => abrirCadastro(c)} color="#F59E0B" bg="rgba(245,158,11,0.15)">
                                <Pencil size={13} />
                              </IconBtn>
                            )}
                            {isAdmin && (
                              <IconBtn title="Inativar" onClick={() => inativarColaborador(c.id)} color={C.danger} bg={`${C.danger}22`}>
                                <Trash2 size={13} />
                              </IconBtn>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                {/* ── Rodapé de totais ─────────────────────────────────── */}
                <tfoot>
                  <tr style={{ background: C.panelBg }}>
                    <td style={{ ...tdFixed(0, C.panelBg), fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.muted, borderTop: `2px solid ${C.border}`, maxWidth: 150, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                      TOTAL
                    </td>
                    <td style={{ ...tdFixed(150, C.panelBg), borderTop: `2px solid ${C.border}`, maxWidth: 130 }}>
                      <span style={{ fontSize: 10, color: C.muted }}>{colaboradoresFiltrados.length} colaborador{colaboradoresFiltrados.length !== 1 ? 'es' : ''}</span>
                    </td>
                    {/* células vazias para Contrato e Vigência */}
                    <td style={{ borderTop: `2px solid ${C.border}`, background: C.panelBg }} />
                    <td style={{ borderTop: `2px solid ${C.border}`, background: C.panelBg }} />
                    {totaisMensais.map((tot, i) => (
                      <td key={i} style={{ padding: '12px 5px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 700, fontSize: 11, color: C.text, borderTop: `2px solid ${C.border}`, background: C.panelBg }}>
                        {loadingBudget ? '…' : fmtK(tot)}
                      </td>
                    ))}
                    <td style={{ padding: '12px 6px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 800, fontSize: 12, color: C.success, borderTop: `2px solid ${C.border}`, background: C.panelBg, whiteSpace: 'nowrap' }}>
                      {loadingBudget ? '…' : fmt(totalGeral)}
                    </td>
                    <td style={{ borderTop: `2px solid ${C.border}`, background: C.panelBg }} />
                  </tr>
                </tfoot>
              </table>
            </div>
            {!isAdmin && depPermitidos && (
              <div style={{ padding: '8px 16px', borderTop: `1px solid ${C.border}`, background: C.panelBg, fontSize: 10, color: C.muted, textAlign: 'right' }}>
                Filtrado por: {depPermitidos.join(', ')}
              </div>
            )}
          </div>
        )}
      </main>

      {/* ── Seção Despesas ────────────────────────────────────────────────── */}
      <section style={{ padding: '24px 24px 24px' }}>
        <p style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, display: 'flex', alignItems: 'center', gap: 8 }}>
          <TrendingUp size={14} /> Despesas
        </p>
        {loadingDespesas ? (
          <Spinner C={C} />
        ) : (
          <div style={{ borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ padding: '11px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.muted, borderBottom: `1px solid ${C.border}`, background: C.panelBg, textAlign: 'left' }}>Categoria</th>
                  <th style={{ padding: '11px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.muted, borderBottom: `1px solid ${C.border}`, background: C.panelBg, textAlign: 'center', minWidth: 110 }}>Lançamentos</th>
                  <th style={{ padding: '11px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.muted, borderBottom: `1px solid ${C.border}`, background: C.panelBg, textAlign: 'right', minWidth: 130 }}>Total</th>
                  <th style={{ padding: '11px 14px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.muted, borderBottom: `1px solid ${C.border}`, background: C.panelBg, textAlign: 'center', width: 80 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {TIPOS_DESPESA.map((categoria, catIdx) => {
                  const itens = despesasFiltradas.filter((d) => d.nome === categoria);
                  const total = itens.reduce((a, d) => a + (d.valor ?? 0), 0);
                  const expandida = categoriasExpandidas.has(categoria);
                  const catBg = catIdx % 2 === 0 ? C.cardBg : C.rowAlt;
                  return (
                    <React.Fragment key={categoria}>
                      {/* ── Linha de categoria ── */}
                      <tr onClick={() => toggleCategoria(categoria)} style={{ cursor: 'pointer', background: catBg }}>
                        <td style={{ padding: '13px 14px', borderTop: `1px solid ${C.border}`, fontWeight: 700 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {expandida
                              ? <ChevronDown size={14} color={C.indigo} />
                              : <ChevronRight size={14} color={C.muted} />}
                            {categoria}
                          </span>
                        </td>
                        <td style={{ padding: '13px 14px', borderTop: `1px solid ${C.border}`, textAlign: 'center' }}>
                          <span style={{ background: `${C.indigo}22`, color: C.indigo, padding: '2px 10px', borderRadius: 999, fontSize: 10, fontWeight: 600 }}>
                            {itens.length} lançamento{itens.length !== 1 ? 's' : ''}
                          </span>
                        </td>
                        <td style={{ padding: '13px 14px', borderTop: `1px solid ${C.border}`, fontFamily: 'monospace', fontWeight: 700, color: total > 0 ? C.text : C.muted, textAlign: 'right' }}>
                          {total > 0 ? fmt(total) : '–'}
                        </td>
                        <td style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, textAlign: 'center' }}>
                          {podeGerenciar && (
                            <IconBtn
                              title={`Novo lançamento em ${categoria}`}
                              onClick={(e) => { e.stopPropagation(); abrirDespesa(null, categoria); }}
                              color={C.success} bg={`${C.success}22`}
                            >
                              <Plus size={13} />
                            </IconBtn>
                          )}
                        </td>
                      </tr>

                      {/* ── Sub-linhas dos lançamentos ── */}
                      {expandida && itens.length === 0 && (
                        <tr>
                          <td colSpan={4} style={{ padding: '10px 14px 10px 44px', color: C.muted, fontSize: 11, fontStyle: 'italic', background: C.deepBg, borderTop: `1px solid ${C.border}` }}>
                            Nenhum lançamento nesta categoria
                          </td>
                        </tr>
                      )}
                      {expandida && itens.map((d) => (
                        <tr key={d.id} style={{ background: C.deepBg }}>
                          <td style={{ padding: '11px 14px 11px 44px', borderTop: `1px solid ${C.border}`, color: C.text }}>
                            {d.descricao
                              ? d.descricao
                              : <span style={{ color: C.muted, fontStyle: 'italic' }}>Sem descrição</span>}
                          </td>
                          <td style={{ padding: '11px 14px', borderTop: `1px solid ${C.border}`, textAlign: 'center' }}>
                            <span style={{ background: `${C.indigo}22`, color: C.indigo, padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {d.departamento}
                            </span>
                          </td>
                          <td style={{ padding: '11px 14px', borderTop: `1px solid ${C.border}`, fontFamily: 'monospace', fontWeight: 600, fontSize: 11, color: C.text, textAlign: 'right' }}>
                            {fmt(d.valor)}
                          </td>
                          <td style={{ padding: '8px 14px', borderTop: `1px solid ${C.border}` }}>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
                              {podeGerenciar && (
                                <IconBtn title="Editar" onClick={() => abrirDespesa(d)} color="#F59E0B" bg="rgba(245,158,11,0.15)">
                                  <Pencil size={13} />
                                </IconBtn>
                              )}
                              {podeGerenciar && (
                                <IconBtn title="Excluir" onClick={() => excluirDespesa(d.id)} color={C.danger} bg={`${C.danger}22`}>
                                  <Trash2 size={13} />
                                </IconBtn>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: C.panelBg }}>
                  <td style={{ padding: '12px 14px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.muted, borderTop: `2px solid ${C.border}` }}>TOTAL</td>
                  <td style={{ padding: '12px 14px', borderTop: `2px solid ${C.border}`, background: C.panelBg, textAlign: 'center' }}>
                    <span style={{ fontSize: 10, color: C.muted }}>{despesasFiltradas.length} lançamento{despesasFiltradas.length !== 1 ? 's' : ''}</span>
                  </td>
                  <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontWeight: 800, fontSize: 13, color: C.success, borderTop: `2px solid ${C.border}`, background: C.panelBg, textAlign: 'right' }}>
                    {fmt(totalDespesas)}
                  </td>
                  <td style={{ borderTop: `2px solid ${C.border}`, background: C.panelBg }} />
                </tr>
              </tfoot>
            </table>
            {!isAdmin && depPermitidos && (
              <div style={{ padding: '8px 16px', borderTop: `1px solid ${C.border}`, background: C.panelBg, fontSize: 10, color: C.muted, textAlign: 'right' }}>
                Filtrado por: {depPermitidos.join(', ')}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Modal Cadastro ─────────────────────────────────────────────────── */}
      {modalCadastro && (
        <Overlay>
          <ModalCard C={C} style={{ maxWidth: 440 }}>
            <ModalHeader title={editando ? 'Editar Colaborador' : 'Novo Colaborador'} C={C} onClose={() => setModalCadastro(false)} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              <Field label="Nome" C={C}>
                <input style={inputStyle} type="text" placeholder="Nome completo"
                  value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </Field>

              <Field label="Salário (R$)" C={C}>
                <input style={inputStyle} type="number" placeholder="0.00" min="0" step="0.01"
                  value={form.salario} onChange={(e) => setForm({ ...form, salario: e.target.value })} />
              </Field>

              {/* Departamento — bloqueado para não-admin com 1 dep; seleção restrita para multi-dep */}
              <Field label={<span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>Departamento{!isAdmin && <Lock size={10} style={{ opacity: 0.6 }} />}</span>} C={C}>
                {isAdmin ? (
                  <select style={inputStyle} value={form.departamento} onChange={(e) => setForm({ ...form, departamento: e.target.value })}>
                    <option value="">Selecione...</option>
                    {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                ) : depPermitidos?.length === 1 ? (
                  <input style={inputLocked} type="text" value={form.departamento} readOnly
                    title="Departamento definido pelo seu perfil de acesso" />
                ) : depPermitidos?.length > 1 ? (
                  <select style={inputStyle} value={form.departamento} onChange={(e) => setForm({ ...form, departamento: e.target.value })}>
                    <option value="">Selecione...</option>
                    {depPermitidos.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                ) : (
                  <input style={inputLocked} type="text" value="Sem acesso" readOnly />
                )}
              </Field>

              {!isAdmin && (
                <p style={{ margin: 0, fontSize: 11, color: C.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Lock size={11} /> Departamento restrito ao seu perfil de acesso.
                </p>
              )}

              <Field label="Tipo de Contrato" C={C}>
                <select style={inputStyle} value={form.tipo_contrato} onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })}>
                  {TIPOS_CONTRATO.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Início da Vigência *" C={C}>
                  <input style={inputStyle} type="month" value={form.data_inicio}
                    onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} />
                </Field>
                <Field label="Fim da Vigência (opcional)" C={C}>
                  <input style={inputStyle} type="month" value={form.data_fim}
                    onChange={(e) => setForm({ ...form, data_fim: e.target.value })} />
                </Field>
              </div>
              <p style={{ margin: '-8px 0 0', fontSize: 10, color: C.muted }}>
                Deixe o Fim em branco para contratos sem prazo de término.
              </p>

              {erro && <p style={{ color: C.danger, fontSize: 12, margin: 0 }}>{erro}</p>}

              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button style={btnSecondary} onClick={() => setModalCadastro(false)}>Cancelar</button>
                <button style={{ ...btnPrimary, opacity: salvando ? 0.6 : 1 }} onClick={salvarColaborador} disabled={salvando}>
                  {salvando ? 'Salvando...' : editando ? 'Atualizar' : 'Cadastrar'}
                </button>
              </div>
            </div>
          </ModalCard>
        </Overlay>
      )}

      {/* ── Modal Simulador ────────────────────────────────────────────────── */}
      {modalSimulador && colaboradorSel && (
        <Overlay>
          <ModalCard C={C} style={{ maxWidth: 820, width: '95vw' }}>
            <ModalHeader
              title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><TrendingUp size={18} color={C.indigo} /> Simulador de Reajuste</span>}
              subtitle={colaboradorSel.nome}
              C={C}
              onClose={() => setModalSimulador(false)}
            />
            {carregandoSim ? <Spinner C={C} /> : (
              <>
                {/* Novo reajuste */}
                <div style={{ background: C.panelBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
                  <p style={{ margin: '0 0 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted }}>Adicionar Reajuste</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
                    <Field label="Mês" C={C}>
                      <select style={inputStyle} value={novoReajuste.mes} onChange={(e) => setNovoReajuste({ ...novoReajuste, mes: parseInt(e.target.value) })}>
                        {MESES_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                      </select>
                    </Field>
                    <Field label="Ano" C={C}>
                      <input style={inputStyle} type="number" value={novoReajuste.ano} onChange={(e) => setNovoReajuste({ ...novoReajuste, ano: parseInt(e.target.value) })} />
                    </Field>
                    <Field label="Enquadramento" C={C}>
                      <select style={inputStyle} value={novoReajuste.enquadramento} onChange={(e) => setNovoReajuste({ ...novoReajuste, enquadramento: parseInt(e.target.value) })}>
                        {Array.from({ length: 15 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>Nível {i + 1}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Agrupamento" C={C}>
                      <select style={inputStyle} value={novoReajuste.agrupamento} onChange={(e) => setNovoReajuste({ ...novoReajuste, agrupamento: e.target.value })}>
                        {TAB_COLUMNS.map((col) => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </Field>
                    <button onClick={salvarReajuste} disabled={salvandoReajuste}
                      style={{ background: C.indigo, border: 'none', borderRadius: 8, padding: '10px 16px', color: '#fff', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap', opacity: salvandoReajuste ? 0.6 : 1, fontFamily: 'inherit' }}>
                      <Save size={14} /> {salvandoReajuste ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                  {(() => {
                    const salPreview = TABELA_REAJUSTE[novoReajuste.agrupamento]?.[novoReajuste.enquadramento - 1];
                    return salPreview ? (
                      <p style={{ margin: '10px 0 0', fontSize: 12, color: C.muted }}>
                        Salário calculado: <span style={{ color: C.success, fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>{fmt(salPreview)}</span>
                      </p>
                    ) : null;
                  })()}
                </div>

                {/* Reajustes cadastrados */}
                {reajustes.filter((r) => r.ano === novoReajuste.ano).length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted }}>
                      Reajustes em {novoReajuste.ano}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {reajustes.filter((r) => r.ano === novoReajuste.ano).map((r) => (
                        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: `${C.indigo}18`, border: `1px solid ${C.indigo}44`, borderRadius: 8, padding: '6px 12px', fontSize: 12 }}>
                          <span style={{ color: C.indigo, fontWeight: 600 }}>{MESES_ABREV[r.mes_reajuste - 1]}</span>
                          <span style={{ color: C.success, fontFamily: 'monospace', fontWeight: 700 }}>{fmt(r.salario_novo)}</span>
                          <button onClick={() => removerReajuste(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 0, display: 'flex', lineHeight: 1 }}>
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tabela semestral */}
                {[0, 1].map((sem) => (
                  <div key={sem} style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: C.panelBg }}>
                          {MESES_ABREV.slice(sem * 6, sem * 6 + 6).map((m) => (
                            <th key={m} style={{ padding: '9px 8px', textAlign: 'center', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                              {m}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {salariosMensais.slice(sem * 6, sem * 6 + 6).map((sal, idx) => {
                            const realIdx = sem * 6 + idx;
                            const anterior = realIdx === 0 ? colaboradorSel.salario : salariosMensais[realIdx - 1];
                            const reajustado = sal > anterior;
                            return (
                              <td key={realIdx} style={{ padding: '12px 6px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 700, fontSize: 11, color: reajustado ? C.success : C.text, background: reajustado ? `${C.success}14` : 'transparent', borderTop: `1px solid ${C.border}` }}>
                                {fmt(sal)}
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}

                {/* Totais */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: C.panelBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 20px' }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted }}>Salário base</p>
                    <p style={{ margin: 0, fontFamily: 'monospace', fontWeight: 700, fontSize: 16, color: C.text }}>{fmt(colaboradorSel.salario)}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted }}>Total anual ({novoReajuste.ano})</p>
                    <p style={{ margin: 0, fontFamily: 'monospace', fontWeight: 700, fontSize: 20, color: C.success }}>{fmt(totalAnual)}</p>
                  </div>
                </div>
              </>
            )}
          </ModalCard>
        </Overlay>
      )}

      {/* ── Modal Despesa ─────────────────────────────────────────────────── */}
      {modalDespesa && (
        <Overlay>
          <ModalCard C={C} style={{ maxWidth: 440 }}>
            <ModalHeader title={editandoDespesa ? 'Editar Despesa' : 'Nova Despesa'} C={C} onClose={() => setModalDespesa(false)} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <Field label="Categoria" C={C}>
              <select
                style={{ ...inputStyle, appearance: 'auto', cursor: editandoDespesa || formDespesa.nome ? 'default' : 'pointer' }}
                value={formDespesa.nome}
                onChange={(e) => setFormDespesa({ ...formDespesa, nome: e.target.value })}
              >
                <option value="">Selecione uma categoria</option>
                {TIPOS_DESPESA.map((tipo) => (
                  <option key={tipo} value={tipo}>{tipo}</option>
                ))}
              </select>
            </Field>

            <Field label="Descrição" C={C}>
              <input
                style={inputStyle}
                type="text"
                placeholder="Ex: Passagem aérea — João Silva"
                value={formDespesa.descricao}
                onChange={(e) => setFormDespesa({ ...formDespesa, descricao: e.target.value })}
              />
            </Field>

            <Field label="Valor (R$)" C={C}>
              <input 
                style={inputStyle} 
                type="number" 
                placeholder="0.00" 
                min="0" 
                step="0.01"
                value={formDespesa.valor} 
                onChange={(e) => setFormDespesa({ ...formDespesa, valor: e.target.value })} 
              />
            </Field>

            <Field 
              label={
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Departamento{!isAdmin && <Lock size={10} style={{ opacity: 0.6 }} />}
                </span>
              } 
              C={C}
            >
              {isAdmin ? (
                <select 
                  style={{ ...inputStyle, appearance: 'auto', cursor: 'pointer' }} // Reaproveitando inputStyle
                  value={formDespesa.departamento} 
                  onChange={(e) => setFormDespesa({ ...formDespesa, departamento: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              ) : depPermitidos?.length === 1 ? (
                <input 
                  style={{ ...inputStyle, backgroundColor: C.background, opacity: 0.8, cursor: 'not-allowed' }} // Simulando o locked com C.background
                  type="text" 
                  value={formDespesa.departamento} 
                  readOnly
                  title="Departamento definido pelo seu perfil de acesso" 
                />
              ) : depPermitidos?.length > 1 ? (
                <select 
                  style={{ ...inputStyle, appearance: 'auto', cursor: 'pointer' }} 
                  value={formDespesa.departamento} 
                  onChange={(e) => setFormDespesa({ ...formDespesa, departamento: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {depPermitidos.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              ) : (
                <input 
                  style={{ ...inputStyle, backgroundColor: C.background, opacity: 0.8 }} 
                  type="text" 
                  value="Sem acesso" 
                  readOnly 
                />
              )}
            </Field>

              {!isAdmin && (
                <p style={{ margin: 0, fontSize: 11, color: C.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Lock size={11} /> Departamento restrito ao seu perfil de acesso.
                </p>
              )}

              {erroDespesa && <p style={{ color: C.danger, fontSize: 12, margin: 0 }}>{erroDespesa}</p>}

              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button style={btnSecondary} onClick={() => setModalDespesa(false)}>Cancelar</button>
                <button style={{ ...btnPrimary, opacity: salvandoDespesa ? 0.6 : 1 }} onClick={salvarDespesa} disabled={salvandoDespesa}>
                  {salvandoDespesa ? 'Salvando...' : editandoDespesa ? 'Atualizar' : 'Cadastrar'}
                </button>
              </div>
            </div>
          </ModalCard>
        </Overlay>
      )}

      {/* ── Modal Tabela de Reajuste ──────────────────────────────────────── */}
      {mostrarModalDetalhamento && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16, animation: 'fadeIn 0.2s ease' }}
          onClick={(e) => { if (e.target === e.currentTarget) setmostrarModalDetalhamento(false); }}
        >
          <div style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: '100%', maxWidth: 960, boxShadow: '0 24px 64px rgba(0,0,0,0.5)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            {/* Cabeçalho */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ background: `${C.indigo}22`, borderRadius: 10, padding: 8, display: 'flex' }}>
                  <Calculator size={20} color={C.indigo} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Tabela de Reajuste</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: C.muted }}>Valores salariais por agrupamento e enquadramento (Nível 1–15)</p>
                </div>
              </div>
              <button onClick={() => setmostrarModalDetalhamento(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 4, display: 'flex', lineHeight: 1 }}>
                <X size={20} />
              </button>
            </div>

            {/* Tabela com scroll */}
            <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, borderRadius: 10, border: `1px solid ${C.border}` }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: 700 }}>
                <thead>
                  <tr style={{ background: C.panelBg }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: C.panelBg, zIndex: 2 }}>
                      Nível
                    </th>
                    {TAB_COLUMNS.map((col) => (
                      <th key={col} style={{ padding: '10px 16px', textAlign: 'center', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.indigo, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap', minWidth: 100 }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {TAB_ROWS.map((rowIdx) => {
                    const rowBg = rowIdx % 2 === 0 ? C.cardBg : C.rowAlt;
                    return (
                      <tr key={rowIdx} style={{ background: rowBg }}>
                        <td style={{ padding: '10px 16px', fontWeight: 700, fontSize: 11, color: C.muted, borderTop: `1px solid ${C.border}`, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: rowBg, zIndex: 1 }}>
                          Nível {rowIdx + 1}
                        </td>
                        {TAB_COLUMNS.map((col) => (
                          <td key={col} style={{ padding: '10px 16px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 600, fontSize: 11, color: C.text, borderTop: `1px solid ${C.border}` }}>
                            {fmt(TABELA_REAJUSTE[col][rowIdx])}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Rodapé */}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button onClick={() => setmostrarModalDetalhamento(false)} style={{ background: C.indigo, border: 'none', borderRadius: 8, padding: '10px 28px', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn  { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
        @media (max-width: 1024px) { .kpi-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 640px)  { .kpi-grid { grid-template-columns: 1fr !important; } }
      `}</style>
      </div>
    </div>
  );
}

// ── Sub-componentes ─────────────────────────────────────────────────────────────

function Spinner({ C }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', border: `3px solid ${C.border}`, borderTopColor: C.indigo, animation: 'spin 0.8s linear infinite' }} />
    </div>
  );
}

function Overlay({ children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
      {children}
    </div>
  );
}

function ModalCard({ children, C, style = {} }) {
  return (
    <div style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.4)', maxHeight: '90vh', overflowY: 'auto', ...style }}>
      {children}
    </div>
  );
}

function ModalHeader({ title, subtitle, C, onClose }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
      <div>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{title}</p>
        {subtitle && <p style={{ margin: '3px 0 0', fontSize: 12, color: C.muted }}>{subtitle}</p>}
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 2, display: 'flex', lineHeight: 1 }}>
        <X size={20} />
      </button>
    </div>
  );
}

function Field({ label, children, C }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.muted }}>{label}</label>
      {children}
    </div>
  );
}

function IconBtn({ children, onClick, title, color, bg }) {
  return (
    <button title={title} onClick={onClick}
      style={{ background: bg, border: 'none', borderRadius: 7, padding: '5px', color, cursor: 'pointer', display: 'flex', alignItems: 'center', lineHeight: 1, transition: 'opacity 0.15s' }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.65')}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
    >
      {children}
    </button>
  );
}
